import { Button, Modal, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { siteConfig, standaloneSite } from "../../site-config/SiteConfig";
import { getStandaloneSpaceId } from "../config";
import { StoreOrderApi } from "../order/StoreOrderApi";
import type { CreateStoreOrderResult } from "../order/StoreOrderType";
import type { MembershipProduct, StrategySkuOffer } from "../../platform_spaces/space/SpaceType";
import "../../platform_spaces/space/SpaceStyle.css";
import "../../platform_spaces/store/StrategyProductPurchaseModal.css";

type Props = {
  row: MembershipProduct;
  open: boolean;
  onClose: () => void;
  prefillStraId?: string;
};

function enabledSpecs(offers: StrategySkuOffer[] | null | undefined, straId: string | null) {
  if (!straId || !offers?.length) return [];
  const hit = offers.find((o) => o.subProductItemId === straId);
  return (hit?.priceSpecs ?? []).filter((s) => s.enabled);
}

/** 独立站：策略市场购买弹窗（参考平台站，收银台走 /tactik/checkout） */
export function StandaloneStrategyProductPurchaseModal(props: Props): JSX.Element {
  const { row, open, onClose, prefillStraId } = props;
  const spaceId = getStandaloneSpaceId();
  const navigate = useNavigate();
  const isMarket = row.strategySaleMode === "market";
  const bundleSpecs = useMemo(
    () => (row.priceSpecs ?? []).filter((s) => s.enabled),
    [row.priceSpecs]
  );
  const offers = useMemo(() => row.strategySkuOffers ?? [], [row.strategySkuOffers]);

  const [straId, setStraId] = useState<string | null>(null);
  const [tierId, setTierId] = useState<string | null>(null);
  const [pendingSign, setPendingSign] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isMarket) {
      const entitled =
        row.strategyEntitlements?.filter((e) => e.active).map((e) => e.subProductItemId) ?? [];
      const pre = prefillStraId?.trim();
      const pick =
        pre && offers.some((o) => o.subProductItemId === pre)
          ? pre
          : entitled.find((id) => offers.some((o) => o.subProductItemId === id)) ??
            offers[0]?.subProductItemId ??
            null;
      setStraId(pick);
      const specs = enabledSpecs(offers, pick);
      setTierId(specs[0]?.tierId ?? null);
    } else {
      setStraId(null);
      setTierId(bundleSpecs[0]?.tierId ?? null);
    }
  }, [open, isMarket, prefillStraId, offers, bundleSpecs, row.strategyEntitlements]);

  const specs = isMarket ? enabledSpecs(offers, straId) : bundleSpecs;

  const maybeSignAndCreate = useCallback(async (): Promise<CreateStoreOrderResult | string> => {
    const tier = tierId ?? specs[0]?.tierId;
    if (!tier) return "请选择规格";
    if (isMarket && !straId) return "请选择策略";
    let res = await StoreOrderApi.createOrder({
      spaceId,
      spaceProductId: row.spaceProductId,
      serviceTier: tier,
      subProductItemId: isMarket ? straId ?? undefined : undefined,
    });
    if (typeof res !== "string" && res.createResult === 401) {
      const needSign = res;
      const tid = row.agreementTemplateId?.trim();
      if (!tid) {
        message.error(needSign.failMessage ?? "请先签署协议");
        return needSign;
      }
      await new Promise<void>((resolve) => {
        Modal.confirm({
          title: "签署购买协议",
          content: needSign.failMessage ?? "需先签署产品购买协议",
          okText: "去签署",
          cancelText: "取消",
          width: 400,
          centered: true,
          className: "lf-modal-square",
          onOk: async () => {
            const signRes = await StoreOrderApi.signAgreement({
              spaceId,
              spaceProductId: row.spaceProductId,
              templateId: tid,
            });
            if (typeof signRes === "string") message.error(signRes);
            else message.success("签署成功");
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
      res = await StoreOrderApi.createOrder({
        spaceId,
        spaceProductId: row.spaceProductId,
        serviceTier: tier,
        subProductItemId: isMarket ? straId ?? undefined : undefined,
      });
    }
    return res;
  }, [isMarket, row, spaceId, specs, straId, tierId]);

  const confirmPurchase = async () => {
    if (isMarket && !straId) {
      message.warning("请先选择策略");
      return;
    }
    if (specs.length > 0 && !tierId) {
      message.warning("请先选择规格");
      return;
    }
    setPendingSign(true);
    try {
      const res = await maybeSignAndCreate();
      if (typeof res === "string") return;
      if (res.createResult !== 0 || !res.orderId) {
        message.error(res.failMessage ?? "下单失败");
        return;
      }
      onClose();
      navigate(`/tactik/checkout/${encodeURIComponent(res.orderId)}`);
    } finally {
      setPendingSign(false);
    }
  };

  const poster =
    row.webPurchasePosterUrl?.trim() || row.mobilePurchasePosterUrl?.trim() || "";
  const brandName = siteConfig.shellBrandNavLabel || standaloneSite.brandName;

  return (
    <Modal
      title={null}
      footer={null}
      closable={false}
      open={open}
      onCancel={onClose}
      width="100%"
      centered={false}
      destroyOnClose
      maskClosable
      className="space-purchase-modal space-purchase-modal--fullscreen"
      wrapClassName="space-purchase-modal-wrap"
      styles={{
        wrapper: { padding: 0 },
        content: {
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
          width: "100vw",
          height: "100vh",
          borderRadius: 0,
          overflow: "hidden",
        },
        body: { padding: 0, height: "100%", overflow: "hidden" },
      }}
    >
      <div className="space-purchase-modal-inner">
        <div className="space-purchase-modal-scroll">
          {poster ? (
            <div
              className="space-purchase-modal-poster"
              style={{ backgroundImage: `url(${poster})` }}
            />
          ) : null}
          <div className="space-purchase-modal-body">
            <div className="space-purchase-modal-space">
              <img
                src={siteConfig.shellLogo}
                alt=""
                className="space-purchase-modal-avatar"
              />
              <div>
                <div className="space-purchase-modal-space-name">{brandName}</div>
                <div className="space-purchase-modal-space-id">{spaceId}</div>
              </div>
            </div>
            <h2 className="space-purchase-modal-product-name">{row.displayName}</h2>
            {isMarket && offers.length > 0 ? (
              <section className="space-purchase-modal-section" aria-label="策略">
                <p className="space-purchase-modal-label">选择策略</p>
                <div className="space-purchase-modal-tier-grid" role="group">
                  {offers.map((o) => {
                    const active = straId === o.subProductItemId;
                    return (
                      <button
                        key={o.subProductItemId}
                        type="button"
                        className={
                          active
                            ? "space-purchase-tier-card space-purchase-tier-card--active"
                            : "space-purchase-tier-card"
                        }
                        onClick={() => {
                          setStraId(o.subProductItemId);
                          const next = enabledSpecs(offers, o.subProductItemId);
                          setTierId(next[0]?.tierId ?? null);
                        }}
                      >
                        <span className="space-purchase-tier-card__name">{o.name}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}
            {specs.length > 0 ? (
              <section className="space-purchase-modal-section" aria-label="规格">
                <p className="space-purchase-modal-label">选择规格</p>
                <div className="space-purchase-modal-tier-grid" role="group">
                  {specs.map((s) => {
                    const active = tierId === s.tierId;
                    return (
                      <button
                        key={s.tierId}
                        type="button"
                        className={
                          active
                            ? "space-purchase-tier-card space-purchase-tier-card--active"
                            : "space-purchase-tier-card"
                        }
                        onClick={() => setTierId(s.tierId)}
                      >
                        <span className="space-purchase-tier-card__name">
                          {s.specName || s.durationLabel}
                        </span>
                        <span className="space-purchase-tier-card__price">¥{s.priceYuan}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
        </div>
        <div className="space-purchase-modal-footer-strip">
          <div className="space-purchase-modal-footer">
            <Button
              className="space-purchase-modal-footer-btn"
              style={{ minWidth: 120 }}
              onClick={onClose}
            >
              取消
            </Button>
            <Button
              type="primary"
              className="space-purchase-modal-footer-btn space-purchase-modal-footer-btn--primary"
              style={{ minWidth: 120 }}
              loading={pendingSign}
              onClick={() => void confirmPurchase()}
            >
              立即订购
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
